import * as React from "react";
import { DatePicker } from "../../../../../../../react-core/Datepicker/DatePicker";
import { SelectEditor } from "../../../../../../../react-core/Html/SelectEditor";
import { TextBoxEditor } from "../../../../../../../react-core/Html/TextBoxEditor";
import { AlphanumericWithSpace } from "../../../../../../../react-core/Html/Validation/RegExPatterns";
import { JsonDateParser } from "../../../../../../../react-core/Utils/JsonDateParser";
import { SubAccountType } from "../../../../../../../Wrap/Constants/Enums/Domain/SubAccountType";
import { ThirdPartyType } from "../../../../../../../Wrap/Constants/Enums/ThirdPartyType";
import { Address } from "../../../../../../Shared/Address/Address";
import { ACCEPT_DATE_FORMATS, ContentDatePicker } from "../../../../../../Shared/Entities/Constants/DateConstants";
import { TitleMap } from "../../../../../../Shared/Entities/Maps/TitleMap";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { ValidatorContents } from "../../../../../Common/ValidatorHelper";
import { ThirdPartyTypeArray, ThirdPartyTypeArrayMap } from "../../../NewBusinessWizard/Resource/Contents";
import { SubAccountTypeHelper } from "../../Helpers/SubAccountTypeHelper";
import { PaymentType } from "../../Resource/Contents";
import { EmployerAddress } from "../../../../../../Shared/Address/EmployerAddress";
import { validationMessage } from "../../../AddClient/Resource/Validation";

interface IThirdPartyEmployerDetailProps extends IProcessComponentProps {
    subAccountType?: SubAccountType;
    paymentType?: PaymentType;
    data: any;
}

interface IThirdPartyEmployerDetailState {
    thirdPartyType: ThirdPartyType;
}

export class ThirdPartyEmployerDetail extends ProcessComponent<
    IThirdPartyEmployerDetailProps,
    IThirdPartyEmployerDetailState
> {
    constructor(props: any) {
        super(props);
        this.state = {
            thirdPartyType: ThirdPartyType.Individual,
        };
    }

    public render(): JSX.Element {
        const { data, onChange, subAccountType, paymentType } = this.props;
        const isSIPP =
            SubAccountTypeHelper.isOnPlatformPension(subAccountType) &&
            (paymentType == PaymentType.Employer || paymentType == PaymentType.EmployerSalaryDeduction);
        const option = ThirdPartyTypeArray.filter((x) => x.toString() == ThirdPartyType.Individual.toString());
        return (
            <>
                {isSIPP == false ? (
                    <div className="row">
                        <div className="col-md-4 col-12">
                            <h3 className="mt-20">Payer details</h3>
                            <SelectEditor
                                label="Third party type"
                                required={"Please select third party type"}
                                selected={data?.Type}
                                options={option}
                                getText={(v) => ThirdPartyTypeArrayMap[v]}
                                onChange={(v) => {
                                    this.setState({ thirdPartyType: v });
                                    onChange({ Type: v });
                                }}
                            />
                        </div>
                    </div>
                ) : null}
                {isSIPP ? this.renderEmployerDetail() : this.renderThirdPartyDetail()}
                {paymentType == PaymentType.ThirdParty ? (
                    <div className="row">
                        <div className="col-md-4 col-12">
                            <Address
                                data={data?.Address}
                                onChange={(v) => {
                                    onChange({
                                        Address: v,
                                    });
                                }}
                                onRef={(ref) => this.appendChild(ref)}
                                required={true}
                            ></Address>
                        </div>
                    </div>
                ) : (
                    <div className="row">
                        <div className="col-md-4 col-12">
                            <EmployerAddress
                                data={data?.Address}
                                onChange={(v) => {
                                    onChange({
                                        Address: v,
                                    });
                                }}
                                onRef={(ref) => this.appendChild(ref)}
                                required={true}
                            ></EmployerAddress>
                        </div>
                    </div>
                )}
            </>
        );
    }

    private renderThirdPartyDetail() {
        const { thirdPartyType } = this.state;
        return (
            <>
                {thirdPartyType == ThirdPartyType.Individual
                    ? this.renderIndividualInfo()
                    : this.renderNonIndividualinfo()}
            </>
        );
    }

    private renderIndividualInfo() {
        const { data, onChange } = this.props;

        return (
            <>
                <div className="row">
                    <div className="col-md-4 col-12">
                        <SelectEditor
                            label="Title"
                            required={"Please select"}
                            selected={data?.Title}
                            options={TitleMap.getArray()}
                            getText={(v) => TitleMap.getString(v)}
                            onChange={(v) => onChange({ Title: v })}
                            placeholder="Please select"
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-4 col-12">
                        <TextBoxEditor
                            label="First name"
                            required="Please enter first name"
                            value={data?.FirstName}
                            maxLength={45}
                            regex={{
                                errorMessage: ValidatorContents.AlphaNumericWithSpace,
                                value: AlphanumericWithSpace,
                            }}
                            onChange={(v) => {
                                onChange({ FirstName: v });
                            }}
                        />
                    </div>
                    <div className="col-md-4 col-12">
                        <TextBoxEditor
                            label="Surname"
                            required="Please enter surname"
                            value={data?.LastName}
                            maxLength={45}
                            regex={{
                                errorMessage: ValidatorContents.AlphaNumericWithSpace,
                                value: AlphanumericWithSpace,
                            }}
                            onChange={(v) => {
                                onChange({ LastName: v });
                            }}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-4 col-12">
                        <DatePicker
                            width="100%"
                            label={"Date of birth"}
                            selected={JsonDateParser.toDateNoOffset(data?.DateOfBirth)}
                            alwaysValidateOnUpdate={false}
                            atLeast={{
                                value: new Date(new Date().setFullYear(new Date().getFullYear() - 120)),
                                errorMessage: ContentDatePicker.CannotBeInThePast,
                            }}
                            atMost={{
                                value: new Date(),
                                errorMessage: ContentDatePicker.CannotBeInTheFuture,
                            }}
                            showYearDropdown={true}
                            onChange={(d) => {
                                onChange({ DateOfBirth: JsonDateParser.toJsonDateNoOffset(d) });
                            }}
                            todayButton={ContentDatePicker.Today}
                            isClearable={true}
                            dateFormat={ContentDatePicker.DateFormat}
                            acceptDateFormats={ACCEPT_DATE_FORMATS}
                            required={validationMessage.dateOfBirthValidation}
                        />
                    </div>
                </div>
            </>
        );
    }

    private renderNonIndividualinfo() {
        const { data, onChange } = this.props;
        return (
            <>
                <div className="row">
                    <div className="col-md-4 col-12">
                        <TextBoxEditor
                            label="Name"
                            required="Please enter name"
                            value={data?.FirstName}
                            maxLength={45}
                            regex={{
                                errorMessage: ValidatorContents.AlphaNumericWithSpace,
                                value: AlphanumericWithSpace,
                            }}
                            onChange={(v) => {
                                onChange({ FirstName: v });
                            }}
                        />
                    </div>
                </div>
            </>
        );
    }

    private renderEmployerDetail() {
        const { data, onChange } = this.props;
        return (
            <div className="row">
                <div className="col-md-4 col-12">
                    <h3 className="mt-20">Payer details</h3>
                    <TextBoxEditor
                        label="Employer name"
                        required="Please enter employer name"
                        value={data?.EmployerName}
                        maxLength={45}
                        regex={{
                            errorMessage: ValidatorContents.AlphaNumericWithSpace,
                            value: AlphanumericWithSpace,
                        }}
                        onChange={(v) => {
                            onChange({ EmployerName: v });
                        }}
                    />
                </div>
            </div>
        );
    }
}
